import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useBookingPrerequisites } from '@/hooks/useBookingPrerequisites';

/**
 * Admin-visible setup prompt that warns owners when the booking page can't show
 * any slots yet because opening hours and/or an available cleaner are missing.
 *
 * Rendered inside AdminLayout so it sits above every tenant admin page. It only
 * shows to owner / super-admin staff (the people who can fix it), and quietly
 * disappears once every prerequisite is in place — so normal users and fully
 * set-up tenants never see it.
 */
export function BookingSetupPrompt() {
  const { staffRole } = useAuth();
  const { orgId } = useAdminOrg();

  // Only the people who can actually complete setup should be nagged about it.
  const canManageSetup = staffRole === 'owner' || staffRole === 'super_admin';

  const { data: items = [], isLoading } = useBookingPrerequisites(canManageSetup ? orgId : null);

  if (!canManageSetup || isLoading) return null;

  const outstanding = items.filter((item) => !item.done);
  if (outstanding.length === 0) return null;

  return (
    <div
      data-testid="booking-setup-prompt"
      role="region"
      aria-label="Booking setup required"
      className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <AlertTriangle className="h-5 w-5 shrink-0 sm:mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Bookings can&rsquo;t show slots until setup is finished</p>
          <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-200/80">
            Customers see &lsquo;no slots available&rsquo; until you complete the steps below.
          </p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {outstanding.map((item) => (
              <li key={item.id} className="text-sm">
                <Link
                  to={item.href}
                  className="group inline-flex items-center gap-1.5 font-medium underline-offset-2 hover:underline"
                >
                  <ArrowRight
                    className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                  <span>{item.title}</span>
                </Link>
                <span className="ml-1 text-xs text-amber-800 dark:text-amber-200/80">
                  — {item.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
